import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { nullIfBlank } from '@shared/utils/payload-utils';
import { InterventionStore } from '../../../intervention.store';
import { ReferrerRequest } from '../../../intervention.model';

// Who referred the member (the "New Request" step): kept with the intervention, edited by internal staff, and not shown to the client or
// provider portals (the server returns an empty referrer to them).
@Component({
  selector: 'intervention-referrer-panel',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './intervention-referrer-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionReferrerPanelComponent {
  protected readonly store = inject(InterventionStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly editing = signal(false);

  protected readonly canEdit = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  protected readonly isEmpty = computed(() => this.store.referrer()?.id == null);

  protected readonly form = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    surname: ['', Validators.maxLength(100)],
    position: ['', Validators.maxLength(100)],
    emailAddress: ['', [Validators.email, Validators.maxLength(320)]],
    officeNumber: ['', Validators.maxLength(50)],
    mobileNumber: ['', Validators.maxLength(50)],
  });

  constructor() {
    effect(() => this.store.loadReferrer(this.interventionId()));
  }

  protected edit(): void {
    const saved = this.store.referrer();
    this.form.reset({
      name: saved?.name ?? '',
      surname: saved?.surname ?? '',
      position: saved?.position ?? '',
      emailAddress: saved?.emailAddress ?? '',
      officeNumber: saved?.officeNumber ?? '',
      mobileNumber: saved?.mobileNumber ?? '',
    });
    this.editing.set(true);
  }

  protected cancel(): void {
    this.editing.set(false);
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const request: ReferrerRequest = {
      name: nullIfBlank(value.name),
      surname: nullIfBlank(value.surname),
      position: nullIfBlank(value.position),
      emailAddress: nullIfBlank(value.emailAddress),
      officeNumber: nullIfBlank(value.officeNumber),
      mobileNumber: nullIfBlank(value.mobileNumber),
      // A link to an employee is kept when the referrer was already linked to one.
      referrerEmployeeId: this.store.referrer()?.referrerEmployeeId ?? null,
    };

    this.store.saveReferrer(this.interventionId(), request, () => this.editing.set(false));
  }

  protected remove(): void {
    this.store.removeReferrer(this.interventionId());
  }
}
